/**
 * proposalApi.js — Hosted Proposal module API client.
 *
 * Thin wrapper over the existing `authFetch` / `apiFetch` patterns used
 * elsewhere in the B2B app. Kept module-local so the module can be
 * vendored, deleted or replaced without touching shared services.
 */

const API = process.env.REACT_APP_BACKEND_URL || "";

async function parseResponse(response) {
  // Defensive parser: read the body via response.clone() so that any
  // upstream observer (PostHog autocapture, service workers, devtools,
  // etc.) that may have already consumed the original stream cannot
  // produce a "body stream already read" error.
  //
  // Empty / 204 / non-JSON / permission-denied bodies all degrade to
  // `payload = null`. Errors prefer backend `detail`/`error`/`message`.
  let payload = null;
  try {
    payload = await response.clone().json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    throw new Error(
      (payload && (payload.detail || payload.error || payload.message)) ||
        `Request failed (${response.status})`,
    );
  }

  return payload;
}

// Back-compat alias — older call sites in this module use `jsonOrThrow`.
const jsonOrThrow = parseResponse;

/* ─────────── Authenticated helpers (B2B users) ─────────── */
function authHeaders() {
  const token = localStorage.getItem("b2b_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function createProposal({
  leadId,
  title,
  proposalType,
  items,
  discount_amount,
  discount_percent,
  pricing_currency,
  category,
  templateId,
}) {
  const body = { lead_id: leadId, title, proposal_type: proposalType };
  if (category) body.category = category;
  if (templateId) body.template_id = templateId;
  if (Array.isArray(items) && items.length > 0) {
    body.items = items;
    if (discount_amount !== undefined) body.discount_amount = discount_amount;
    if (discount_percent !== undefined) body.discount_percent = discount_percent;
    if (pricing_currency !== undefined) body.pricing_currency = pricing_currency;
  }
  const res = await fetch(`${API}/api/proposals`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

export async function listProposals({ leadId } = {}) {
  const qs = leadId ? `?lead_id=${encodeURIComponent(leadId)}` : "";
  const res = await fetch(`${API}/api/proposals${qs}`, { headers: authHeaders() });
  return jsonOrThrow(res);
}

export async function getProposal(id) {
  const res = await fetch(`${API}/api/proposals/${id}`, { headers: authHeaders() });
  return jsonOrThrow(res);
}

export async function updateProposal(id, patch) {
  const res = await fetch(`${API}/api/proposals/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(patch),
  });
  return jsonOrThrow(res);
}

export async function publishProposal(id) {
  const res = await fetch(`${API}/api/proposals/${id}/publish`, {
    method: "POST",
    headers: authHeaders(),
  });
  return jsonOrThrow(res);
}

export async function logProposalEvent(id, eventType, metadata) {
  try {
    await fetch(`${API}/api/proposals/${id}/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ event_type: eventType, metadata }),
    });
  } catch {
    /* fire-and-forget — never block the UX on telemetry */
  }
}

export function pdfDownloadUrl(id) {
  // Server uses Authorization header — we trigger the download via a fetch
  // and Blob URL rather than a raw <a href>.
  return `${API}/api/proposals/${id}/pdf`;
}

export async function downloadProposalPdf(id, filename = "proposal.pdf") {
  const res = await fetch(pdfDownloadUrl(id), { headers: authHeaders() });
  if (!res.ok) throw new Error(`PDF generation failed (${res.status})`);
  // Clone so observers (PostHog, service workers) that may have peeked
  // at the stream don't cause "body stream already read" on .blob().
  const blob = await res.clone().blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function uploadPartnerLogo(partnerId, file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API}/api/proposals/branding/partners/${partnerId}/logo`, {
    method: "POST",
    headers: authHeaders(), // do NOT set Content-Type — browser sets multipart boundary
    body: fd,
  });
  return jsonOrThrow(res);
}

export async function updatePartnerBranding(partnerId, branding) {
  const res = await fetch(`${API}/api/proposals/branding/partners/${partnerId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(branding),
  });
  return jsonOrThrow(res);
}

/* ─────────── Public viewer (no auth) ─────────── */
export async function getPublicProposal(token) {
  const res = await fetch(`${API}/api/proposals/public/${token}`);
  if (res.status === 410) {
    const err = new Error("expired");
    err.code = "expired";
    throw err;
  }
  if (res.status === 404) {
    const err = new Error("not_found");
    err.code = "not_found";
    throw err;
  }
  return jsonOrThrow(res);
}

export function publicProposalUrl(token) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/p/${token}`;
}
